import { useState } from "react";
import { X, FileText, Download } from "lucide-react";
import { Button } from "../ui/button";

type Invoice = {
  id: string;
  documentNumber: string | null;
  documentType: string;
};

export function InvoicesModal({
  invoices,
  organizationSlug,
  onClose,
}: {
  invoices: Invoice[];
  organizationSlug: string;
  onClose: () => void;
}) {
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  const handleDownload = async (documentId: string) => {
    setDownloadingId(documentId);
    try {
      const api = import.meta.env.VITE_API_URL || "http://localhost:9000";
      const res = await fetch(`${api}/api/organizations/${organizationSlug}/accounting/records/${documentId}/download-url`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error("Kunne ikke hente nedlastingslenke");
      const { downloadUrl } = await res.json();
      window.open(downloadUrl, "_blank");
    } catch (err) {
      console.error("Failed to download invoice", err);
      alert("Kunne ikke laste ned fakturaen. Vennligst prøv igjen senere.");
    } finally {
      setDownloadingId(null);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4 sm:p-6"
      style={{ background: "rgba(10, 25, 30, 0.4)", backdropFilter: "blur(8px)" }}
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-[2rem] bg-white shadow-2xl overflow-hidden animate-in fade-in slide-in-from-bottom-8 duration-500"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="relative px-8 pt-8 pb-6">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-2xl font-black text-[#1a2e35]" style={{ fontFamily: "var(--font-display)" }}>
              Velg faktura
            </h2>
            <button
              onClick={onClose}
              className="flex h-10 w-10 items-center justify-center rounded-full bg-muted/50 hover:bg-muted transition-all active:scale-95"
            >
              <X className="h-5 w-5 text-muted-foreground" />
            </button>
          </div>
          <p className="text-sm text-balance text-muted-foreground leading-relaxed">
            Denne auksjonen har flere fakturaer knyttet til seg. Velg hvilken du vil laste ned.
          </p>
        </div>

        {/* Body */}
        <div className="px-8 pb-8 space-y-3 max-h-[60vh] overflow-y-auto custom-scrollbar">
          {invoices.map((inv) => (
            <div
              key={inv.id}
              className="flex items-center justify-between p-4 rounded-2xl border border-border/50 bg-muted/20"
            >
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/5 text-primary">
                  <FileText className="h-5 w-5" />
                </div>
                <div>
                  <div className="text-sm font-bold text-[#1a2e35]">
                    {inv.documentNumber || "Utkast"}
                  </div>
                  <div className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">
                    {inv.documentType === "credit_note" ? "Kreditnota" : "Faktura"}
                  </div>
                </div>
              </div>
              <Button
                size="sm"
                variant="ghost"
                disabled={downloadingId === inv.id}
                onClick={() => handleDownload(inv.id)}
                className="h-9 w-9 p-0 rounded-full hover:bg-primary/5 text-primary transition-all active:scale-90"
              >
                {downloadingId === inv.id ? (
                  <div className="h-4 w-4 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
                ) : (
                  <Download className="h-4 w-4" />
                )}
              </Button>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="px-8 pb-8 pt-2">
          <Button
            variant="ghost"
            onClick={onClose}
            className="w-full h-12 rounded-2xl font-bold text-muted-foreground hover:bg-muted transition-all"
          >
            Lukk
          </Button>
        </div>
      </div>
    </div>
  );
}
